import React, { useEffect, useRef, useState } from 'react'

import { functions } from '@wap-client/core'
import { useApp } from '@wap-client/core'
import { useMobile } from '@wap-client/core'

import Anchor from '@/components/base/anchor'
import Icon from '@/components/base/icon'
import Slideshow from '@/components/widgets/slideshow'
import Image from '@/components/base/image'

import {
  SustainabilitySlideshowItem,
  SustainabilitySlideshowProps,
} from './types'
import SocialMedia from '@/components/widgets/social-media'

const SustainabilitySlideshow: React.FunctionComponent<
  SustainabilitySlideshowProps
> = ({ data = [] }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const app = useApp()
  const ref = useRef<HTMLDivElement>(null)
  const isMobile = useMobile(app.headers['user-agent'])

  useEffect(() => {
    if (ref.current) {
      ref.current.style.setProperty('--index', '0')
    }
  }, [])

  const handleSlideChange = (index: number) => {
    const totalIndex = data.length - 1

    if (index > totalIndex || index < 0) {
      index = 0
    }

    ref.current && ref.current.style.setProperty('--index', index.toString())
    setActiveIndex(index)
  }

  const renderTools = () => {
    if (data.length <= 4) return null

    return (
      <div className="sustainability-slideshow-buttons">
        {activeIndex > 0 && (
          <button
            className="sustainability-slideshow-button sustainability-slideshow-button--prev"
            onClick={() => handleSlideChange(activeIndex - 1)}
          >
            <Icon name="arrow-left" />
          </button>
        )}
        <button
          className="sustainability-slideshow-button sustainability-slideshow-button--next"
          onClick={() => handleSlideChange(activeIndex + 1)}
        >
          <Icon name="arrow-right" />
        </button>
      </div>
    )
  }

  const renderCard = (card: SustainabilitySlideshowItem, index: number) => {
    if (!card) return null

    const renderCaption = (className?: string) => (
      <div
        className={functions.classnames(
          'sustainability-card-caption',
          className,
        )}
      >
        <SocialMedia />
        <Anchor {...card.anchor}>
          <h4>{card.title}</h4>
          <p>{card.description}</p>
        </Anchor>
      </div>
    )

    return (
      <div key={index} className="sustainability-card">
        <div className="sustainability-card-image">
          <Anchor {...card.anchor}>
            <Image {...card.image} alt={card.title} />
          </Anchor>
        </div>

        {renderCaption()}
        {renderCaption('sustainability-card-caption--dynamic')}
      </div>
    )
  }

  const renderSlide = (
    card: SustainabilitySlideshowItem,
    index: number,
    isClone?: boolean,
  ) => {
    if (!card) return null

    return (
      <div
        data-current={index === activeIndex}
        key={index}
        className={functions.classnames(
          'sustainability-slideshow-item',
          isClone && 'sustainability-slideshow-item--cloned',
        )}
      >
        {renderCard(card, index)}
      </div>
    )
  }

  if (!data || !data.length) return null

  return (
    <div className="sustainability-slideshow" ref={ref}>
      {isMobile ? (
        <Slideshow settings={{ infinite: true }}>
          {data.map((card, index) => renderSlide(card, index))}
        </Slideshow>
      ) : (
        <React.Fragment>
          <div className="sustainability-slideshow-tracker">
            {data.map((card, index) => renderSlide(card, index))}
            {data.length > 4
              ? [...Array(3)].map((_, index) =>
                  renderSlide(data[index], index, true),
                )
              : null}
          </div>
          {renderTools()}
        </React.Fragment>
      )}
    </div>
  )
}

export default SustainabilitySlideshow
